"use client";

import { useTranslations } from "next-intl";
import { useLayoutEffect, useMemo, useState, useEffect, useRef } from "react";
import Link from "next/link";
import { X, ChevronDown, Search, Sparkles, Users, Clock, Compass, User as UserIcon } from "lucide-react";
import { createClient } from "@/utils/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getTaxonomyItems } from "@/lib/taxonomy-cache";
import { cn } from "@/lib/utils";
import { ROUTE_GUTTER_CLASS, ROUTE_MAX_WIDTH_CLASS } from "@/lib/route-layout";
import { motion, AnimatePresence } from "framer-motion";
import {
  CONDITION_GRADE_MAX,
  CONDITION_GRADE_MIN,
  CONDITION_GRADE_LABELS,
  GradeRangeFilterPanel,
} from "@/components/feed/grade-range-filter-panel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AnimatedSearchBar } from "./animated-search-bar";
import { HeaderActions } from "@/components/feed/header-actions";
import type { FeedActiveTab } from "./feed-page-client-state";
import {
  FEED_FOR_YOU_TAB_ENABLED,
  FEED_TAB_DISPLAY_ORDER,
  isFeedTabVisible,
} from "@/lib/feed-tab-config";

interface FilterOption {
  id: string;
  label: string;
}

const CONDITIONS = [
  { value: "new", label: "new" },
  { value: "pre-owned", label: "preOwned" },
  { value: "custom", label: "custom" },
];

interface FeedTopBarProps {
  activeTab: FeedActiveTab;
  onTabChangeAction: (tab: FeedActiveTab) => void;
  isLoggedIn: boolean;
  onSearchAction: (query: string) => void;
  currentUserId?: string;
  onFiltersChangeAction: (filters: {
    brands: string[];
    themes: string[];
    categories: string[];
    conditions: string[];
    gradeRange: [number, number] | null;
    sortBy: 'price-asc' | 'price-desc' | 'date-asc' | 'date-desc' | null;
  }) => void;
  activeTabForFilters?: FeedActiveTab;
  snapStyleNav?: boolean;
}

export function FeedTopBar({
  activeTab,
  onTabChangeAction,
  isLoggedIn,
  onSearchAction,
  currentUserId,
  onFiltersChangeAction,
  activeTabForFilters = "live",
  snapStyleNav = false,
}: FeedTopBarProps) {
  const tFeed = useTranslations("feedTabs");
  const supabase = useMemo(() => createClient(), []);
  const headerRef = useRef<HTMLElement | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  // Desktop persistent search field (mockup: "Find your white whale…" beside
  // the tabs). Mobile keeps the collapsible AnimatedSearchBar.
  const [desktopQuery, setDesktopQuery] = useState("");
  const [isSearchLayoutExpanded, setIsSearchLayoutExpanded] = useState(false);
  const searchLayoutTimerRef = useRef<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [brands, setBrands] = useState<FilterOption[]>([]);
  const [themes, setThemes] = useState<FilterOption[]>([]);
  const [categories, setCategories] = useState<FilterOption[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [gradeRange, setGradeRange] = useState<[number, number] | null>(null);
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'date-asc' | 'date-desc' | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // Mobile header avatar (mockup): current user's avatar → /profile.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const lastOpenedRef = useRef<{ key: string; at: number } | null>(null);
  const filtersDragRef = useRef<{ active: boolean; startX: number; startY: number }>({
    active: false,
    startX: 0,
    startY: 0,
  });
  const filtersDraggingRef = useRef(false);
  const handleSearchModeChange = (next: boolean) => {
    setIsSearchMode(next);
    if (searchLayoutTimerRef.current) {
      window.clearTimeout(searchLayoutTimerRef.current);
      searchLayoutTimerRef.current = null;
    }
    if (next) {
      setIsSearchLayoutExpanded(true);
      return;
    }
    searchLayoutTimerRef.current = window.setTimeout(() => {
      setIsSearchLayoutExpanded(false);
      searchLayoutTimerRef.current = null;
    }, 260);
  };
  const clearFiltersDraggingSoon = () => {
    window.setTimeout(() => {
      filtersDraggingRef.current = false;
      filtersDragRef.current.active = false;
    }, 0);
  };
  const handleFiltersPointerDown = (event: any) => {
    if (event?.pointerType !== "touch") return;
    filtersDragRef.current.active = true;
    filtersDragRef.current.startX = event.clientX ?? 0;
    filtersDragRef.current.startY = event.clientY ?? 0;
    filtersDraggingRef.current = false;
  };
  const handleFiltersPointerMove = (event: any) => {
    if (event?.pointerType !== "touch") return;
    if (!filtersDragRef.current.active) return;
    if (filtersDraggingRef.current) return;
    const dx = (event.clientX ?? 0) - filtersDragRef.current.startX;
    const dy = (event.clientY ?? 0) - filtersDragRef.current.startY;
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      filtersDraggingRef.current = true;
    }
  };
  const handleFiltersPointerUp = () => {
    clearFiltersDraggingSoon();
  };
  const handleFiltersPointerCancel = () => {
    clearFiltersDraggingSoon();
  };
  const blockClickIfDragging = (event: any) => {
    if (!filtersDraggingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      root.style.setProperty("--feed-top-bar-height", `${h}px`);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener("resize", update, { passive: true } as any);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update as any);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchTaxonomy() {
      let brandsData, themesData, categoriesData;
      try {
        [brandsData, themesData, categoriesData] = await Promise.all([
          getTaxonomyItems({ type: "brand" }),
          getTaxonomyItems({ type: "theme" }),
          getTaxonomyItems({ type: "product_type" }),
        ]);
      } catch {
        return; // leave options empty on load failure (prior behavior)
      }
      if (cancelled) return;

      const uniqueBrands = Array.from(
        new Map(brandsData.map((b) => [b.name.toLowerCase(), b])).values()
      );
      setBrands(uniqueBrands.map((b) => ({ id: b.id, label: b.name })));

      const uniqueThemes = Array.from(
        new Map(themesData.map((t) => [t.name.toLowerCase(), t])).values()
      );
      setThemes(uniqueThemes.map((t) => ({ id: t.id, label: t.name })));

      const uniqueCategories = Array.from(
        new Map(categoriesData.map((c) => [c.name.toLowerCase(), c])).values()
      );
      setCategories(uniqueCategories.map((c) => ({ id: c.id, label: c.name })));
    }

    fetchTaxonomy();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) {
      setAvatarUrl(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("avatar_url")
      .eq("id", currentUserId)
      .single()
      .then(({ data }) => {
        if (!cancelled) setAvatarUrl(data?.avatar_url ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, supabase]);

  useEffect(() => {
    return () => {
      if (!searchLayoutTimerRef.current) return;
      window.clearTimeout(searchLayoutTimerRef.current);
      searchLayoutTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    onFiltersChangeAction({
      brands: selectedBrands,
      themes: selectedThemes,
      categories: selectedCategories,
      conditions: selectedConditions,
      gradeRange,
      sortBy,
    });
  }, [selectedBrands, selectedThemes, selectedCategories, selectedConditions, gradeRange, sortBy, onFiltersChangeAction]);

  const tTabs = useTranslations("feedTabs");
  const tFilters = useTranslations("feedFilters");
  const tabMeta: Record<
    FeedActiveTab,
    { label: string; title?: string; requiresAuth?: boolean }
  > = {
    "for-you": { label: tTabs("forYou"), title: tTabs("forYouTitle") },
    following: {
      label: tTabs("following"),
      requiresAuth: true,
      title: tTabs("followingTitle"),
    },
    live: { label: tTabs("latest"), title: tTabs("latestTitle") },
    discover: { label: tTabs("discover"), title: tTabs("discoverTitle") },
  };

  // Mockup mobile tab-menu icons (For You / Following / Latest / Discover).
  const tabIcon: Record<FeedActiveTab, typeof Sparkles> = {
    "for-you": Sparkles,
    following: Users,
    live: Clock,
    discover: Compass,
  };

  const visibleTabs = FEED_TAB_DISPLAY_ORDER.filter((key) =>
    isFeedTabVisible(key, isLoggedIn)
  ).map((key) => ({
    key,
    ...tabMeta[key],
  }));

  const activeTabLabel = tabMeta[activeTab]?.label ?? tabMeta.live.label;

  const hasActiveFilters =
    selectedBrands.length > 0 ||
    selectedThemes.length > 0 ||
    selectedCategories.length > 0 ||
    selectedConditions.length > 0 ||
    gradeRange !== null ||
    sortBy !== null;

  const effectiveGradeRange = gradeRange || [CONDITION_GRADE_MIN, CONDITION_GRADE_MAX];

  const toggleBrand = (id: string) => {
    setSelectedBrands((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  };

  const toggleTheme = (id: string) => {
    setSelectedThemes((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleCondition = (value: string) => {
    setSelectedConditions((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const clearAllFilters = () => {
    setSelectedBrands([]);
    setSelectedThemes([]);
    setSelectedCategories([]);
    setSelectedConditions([]);
    setGradeRange(null);
    setSortBy(null);
  };

  const filterControls = (
    <>
      <DropdownMenu
        modal={false}
        open={openDropdown === "brands"}
        onOpenChange={(open) => {
          if (open) {
            lastOpenedRef.current = { key: "brands", at: Date.now() };
            setOpenDropdown("brands");
            return;
          }
          setOpenDropdown((prev) => {
            const last = lastOpenedRef.current;
            if (last?.key === "brands" && Date.now() - last.at < 120) {
              return prev;
            }
            return prev === "brands" ? null : prev;
          });
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClickCapture={blockClickIfDragging}
            className={cn(
              "pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm hover:shadow-md",
              selectedBrands.length > 0
                ? "bg-background/50 text-foreground border-border/40"
                : "bg-background/30 text-foreground border-border/20"
            )}
          >
            <span>{tFilters("brand")}</span>
            {selectedBrands.length > 0 && (
              <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-foreground/20 text-foreground">
                {selectedBrands.length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          surface="glass"
          className="w-56 md:max-h-[300px] max-h-[60vh] overflow-y-auto scrollbar-hide"
        >
          {selectedBrands.length > 0 && (
            <div className="flex items-center justify-end px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBrands([]);
                }}
                className="text-xs font-semibold text-foreground hover:opacity-80 dark:text-white dark:hover:text-white/80"
              >
                Clear
              </button>
            </div>
          )}
          {brands.map((brand) => (
            <DropdownMenuCheckboxItem
              key={brand.id}
              checked={selectedBrands.includes(brand.id)}
              onCheckedChange={() => toggleBrand(brand.id)}
            >
              {brand.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu
        modal={false}
        open={openDropdown === "themes"}
        onOpenChange={(open) => {
          if (open) {
            lastOpenedRef.current = { key: "themes", at: Date.now() };
            setOpenDropdown("themes");
            return;
          }
          setOpenDropdown((prev) => {
            const last = lastOpenedRef.current;
            if (last?.key === "themes" && Date.now() - last.at < 120) {
              return prev;
            }
            return prev === "themes" ? null : prev;
          });
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClickCapture={blockClickIfDragging}
            className={cn(
              "pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm hover:shadow-md",
              selectedThemes.length > 0
                ? "bg-background/50 text-foreground border-border/40"
                : "bg-background/30 text-foreground border-border/20"
            )}
          >
            <span>{tFilters("theme")}</span>
            {selectedThemes.length > 0 && (
              <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-foreground/20 text-foreground">
                {selectedThemes.length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          surface="glass"
          className="w-56 md:max-h-[300px] max-h-[60vh] overflow-y-auto scrollbar-hide"
        >
          {selectedThemes.length > 0 && (
            <div className="flex items-center justify-end px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedThemes([]);
                }}
                className="text-xs font-semibold text-foreground hover:opacity-80 dark:text-white dark:hover:text-white/80"
              >
                Clear
              </button>
            </div>
          )}
          {themes.map((theme) => (
            <DropdownMenuCheckboxItem
              key={theme.id}
              checked={selectedThemes.includes(theme.id)}
              onCheckedChange={() => toggleTheme(theme.id)}
            >
              {theme.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu
        modal={false}
        open={openDropdown === "conditions"}
        onOpenChange={(open) => {
          if (open) {
            lastOpenedRef.current = { key: "conditions", at: Date.now() };
            setOpenDropdown("conditions");
            return;
          }
          setOpenDropdown((prev) => {
            const last = lastOpenedRef.current;
            if (last?.key === "conditions" && Date.now() - last.at < 120) {
              return prev;
            }
            return prev === "conditions" ? null : prev;
          });
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClickCapture={blockClickIfDragging}
            className={cn(
              "pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm hover:shadow-md",
              selectedConditions.length > 0
                ? "bg-background/50 text-foreground border-border/40"
                : "bg-background/30 text-foreground border-border/20"
            )}
          >
            <span>{tFilters("condition")}</span>
            {selectedConditions.length > 0 && (
              <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-foreground/20 text-foreground">
                {selectedConditions.length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" surface="glass" className="w-56 scrollbar-hide">
          {selectedConditions.length > 0 && (
            <div className="flex items-center justify-end px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedConditions([]);
                }}
                className="text-xs font-semibold text-foreground hover:opacity-80 dark:text-white dark:hover:text-white/80"
              >
                Clear
              </button>
            </div>
          )}
          {CONDITIONS.map((condition) => (
            <DropdownMenuCheckboxItem
              key={condition.value}
              checked={selectedConditions.includes(condition.value)}
              onCheckedChange={() => toggleCondition(condition.value)}
            >
              {tFilters(condition.label as any)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu
        modal={false}
        open={openDropdown === "grading"}
        onOpenChange={(open) => {
          if (open) {
            lastOpenedRef.current = { key: "grading", at: Date.now() };
            setOpenDropdown("grading");
            return;
          }
          setOpenDropdown((prev) => {
            const last = lastOpenedRef.current;
            if (last?.key === "grading" && Date.now() - last.at < 120) {
              return prev;
            }
            return prev === "grading" ? null : prev;
          });
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClickCapture={blockClickIfDragging}
            className={cn(
              "pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm hover:shadow-md",
              gradeRange
                ? "bg-background/50 text-foreground border-border/40"
                : "bg-background/30 text-foreground border-border/20"
            )}
          >
            <span>{tFilters("grading")}</span>
            {gradeRange && (
              <span className="text-xs">
                {gradeRange[0]}-{gradeRange[1]}
              </span>
            )}
            <ChevronDown className={cn("h-3 w-3 transition-transform", openDropdown === "grading" && "rotate-180")} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          surface="glass"
          className="w-[24rem] p-4"
        >
          <GradeRangeFilterPanel
            value={effectiveGradeRange}
            min={CONDITION_GRADE_MIN}
            max={CONDITION_GRADE_MAX}
            step={1}
            onValueChange={(next) => setGradeRange([next[0], next[1]])}
            onClear={() => setGradeRange(null)}
            clearLabel="Clear"
            clearButtonClassName="text-xs font-semibold text-foreground hover:opacity-80 dark:text-white dark:hover:text-white/80"
            hint={tFilters("gradeRangeHint")}
            labels={CONDITION_GRADE_LABELS}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        type="button"
        onClickCapture={blockClickIfDragging}
        onClick={() => {
          if (sortBy === "price-asc") {
            setSortBy("price-desc");
          } else if (sortBy === "price-desc") {
            setSortBy(null);
          } else {
            setSortBy("price-asc");
          }
        }}
        className={cn(
          "pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm hover:shadow-md",
          sortBy?.startsWith("price")
            ? "bg-background/50 text-foreground border-border/40"
            : "bg-background/30 text-foreground border-border/20"
        )}
      >
        <span>{tFilters("price")}</span>
        {sortBy === "price-asc" && <span className="text-xs">↑</span>}
        {sortBy === "price-desc" && <span className="text-xs">↓</span>}
      </button>

      {(activeTabForFilters === "live" || activeTabForFilters === "discover") && (
        <button
          type="button"
          onClickCapture={blockClickIfDragging}
          onClick={() => {
            if (sortBy === "date-asc") {
              setSortBy("date-desc");
            } else if (sortBy === "date-desc") {
              setSortBy(null);
            } else {
              setSortBy("date-asc");
            }
          }}
          className={cn(
            "pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm hover:shadow-md",
            sortBy?.startsWith("date")
              ? "bg-background/50 text-foreground border-border/40"
              : "bg-background/30 text-foreground border-border/20"
          )}
        >
          <span>{tFilters("listed")}</span>
          {sortBy === "date-asc" && <span className="text-xs">↑</span>}
          {sortBy === "date-desc" && <span className="text-xs">↓</span>}
        </button>
      )}

      {hasActiveFilters && (
        <button
          type="button"
          onClickCapture={blockClickIfDragging}
          onClick={clearAllFilters}
          className="pointer-events-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm font-medium transition-all duration-200 border backdrop-blur-md shadow-sm bg-destructive/20 text-destructive border-destructive/40"
        >
          <X className="h-3 w-3" />
          <span>{tFilters("clear")}</span>
        </button>
      )}
    </>
  );

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 safe-area-top pointer-events-none isolate"
      data-feed-top-bar
      data-search-mode={isSearchMode ? "true" : "false"}
    >
      <div
        aria-hidden
        style={{
          maskImage: "linear-gradient(to bottom, #000 0%, #000 calc(100% - 0.625rem), transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 calc(100% - 0.625rem), transparent 100%)",
          maskSize: "100% 100%",
          WebkitMaskSize: "100% 100%",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
        }}
        className={cn(
          "absolute inset-0 z-0 pointer-events-none",
          snapStyleNav
            ? "bg-linear-to-b from-black/50 via-black/22 to-transparent backdrop-blur-md"
            : "bg-linear-to-b from-background/82 via-background/42 to-transparent backdrop-blur-md dark:from-background/78 dark:via-background/36"
        )}
      />
      <div className="relative z-1">
        <div className={cn("relative flex items-center gap-2 h-14 md:h-14 md:grid md:grid-cols-[auto_auto_1fr_auto] md:gap-5 pointer-events-none", ROUTE_GUTTER_CLASS)}>
          <div
            className={cn(
              "shrink-0 transition-all duration-200 md:justify-self-start",
              isSearchMode ? "opacity-0 w-0 overflow-hidden md:opacity-100 md:w-auto md:overflow-visible" : "opacity-100"
            )}
          >
            <Link
              href="/feed"
              className={cn(
                "pointer-events-auto font-display text-2xl font-bold tracking-[-0.04em] whitespace-nowrap md:text-2xl",
                snapStyleNav ? "text-white" : "text-foreground"
              )}
            >
              Kraahl
            </Link>
          </div>
          <div
            className={cn(
              "flex-1 min-w-0 transition-all duration-200 md:w-auto md:flex-none md:justify-self-start",
              isSearchLayoutExpanded
                ? "opacity-0 pointer-events-none md:hidden"
                : "opacity-100 pointer-events-auto"
            )}
          >
            {/* Mobile: centered tab dropdown (mockup "For You ▾"). */}
            <div className="flex w-full justify-center md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Change feed"
                    className={cn(
                      "pointer-events-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5",
                      snapStyleNav ? "text-white" : "text-foreground"
                    )}
                  >
                    <span className="text-base font-bold tracking-[-0.02em]">{activeTabLabel}</span>
                    <ChevronDown className="size-[17px]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" surface="glass">
                  {visibleTabs.map(({ key, label }) => {
                    const Icon = tabIcon[key];
                    return (
                      <DropdownMenuCheckboxItem
                        key={key}
                        checked={activeTab === key}
                        onCheckedChange={() => onTabChangeAction(key)}
                      >
                        <Icon className="mr-2.5 size-[18px] shrink-0" />
                        {label}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* Desktop: pill tabs beside the wordmark (mockup). */}
            <motion.div
              key="tabs"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
              className="hidden w-auto min-h-8 items-center justify-start gap-1 pointer-events-auto md:flex"
            >
              {visibleTabs.map(({ key, label, title }) => (
                <button
                  key={key}
                  type="button"
                  title={title}
                  onClick={() => onTabChangeAction(key)}
                  className={cn(
                    "pointer-events-auto inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-150",
                    snapStyleNav
                      ? activeTab === key
                        ? "bg-white/20 text-white"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                      : activeTab === key
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                  )}
                >
                  {label}
                </button>
              ))}
            </motion.div>
          </div>
          <div
            className={cn(
              "pointer-events-auto md:relative md:h-10 shrink-0",
              isSearchLayoutExpanded
                ? "md:col-start-2 md:col-span-2 md:w-full md:justify-self-stretch"
                : "md:col-start-3 md:col-span-1 md:w-full md:max-w-[460px] md:justify-self-center"
            )}
          >
            {/* Mobile: collapsible search (unchanged). */}
            <div className="md:hidden">
              <AnimatedSearchBar
                onSearch={onSearchAction}
                onSearchModeChange={handleSearchModeChange}
                overlayPaddingOffset={18}
                onDarkMedia={snapStyleNav}
                collapsedRightOffset={44}
              />
            </div>
            {/* Desktop: persistent search field beside the tabs (mockup). */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const q = desktopQuery.trim();
                if (q) onSearchAction(q);
              }}
              className={cn(
                "hidden h-11 w-full items-center gap-2.5 rounded-full border px-4 transition-colors md:flex",
                snapStyleNav
                  ? "border-white/30 bg-white/15 backdrop-blur-md focus-within:ring-2 focus-within:ring-white/30"
                  : "border-border/70 bg-secondary focus-within:border-foreground/30 focus-within:ring-2 focus-within:ring-ring/30",
              )}
            >
              <Search
                aria-hidden
                className={cn(
                  "size-[18px] shrink-0",
                  snapStyleNav ? "text-white/70" : "text-muted-foreground",
                )}
              />
              <input
                type="search"
                value={desktopQuery}
                onChange={(e) => setDesktopQuery(e.target.value)}
                placeholder={tFeed("searchPlaceholder")}
                aria-label={tFeed("searchLabel")}
                className={cn(
                  "w-full bg-transparent text-sm outline-none",
                  snapStyleNav ? "text-white placeholder:text-white/50" : "text-foreground placeholder:text-muted-foreground",
                )}
              />
            </form>
          </div>
          {/* Mobile: avatar → profile (mockup). Desktop uses the HeaderActions
              cluster; on mobile the bottom nav owns primary navigation. */}
          <Link
            href={isLoggedIn ? "/profile" : "/login"}
            aria-label={isLoggedIn ? "Profile" : "Log in"}
            className={cn(
              "pointer-events-auto shrink-0 transition-all duration-200 md:hidden",
              isSearchMode ? "w-0 overflow-hidden opacity-0" : "opacity-100"
            )}
          >
            <Avatar className="size-9 border border-border/60">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
              <AvatarFallback
                className={cn(
                  snapStyleNav ? "bg-white/15 text-white" : "bg-secondary text-muted-foreground"
                )}
              >
                <UserIcon className="size-[18px]" />
              </AvatarFallback>
            </Avatar>
          </Link>
          {/* Right cluster (mockup header). Logged-out: Log In + Get Started
              foil. Logged-in cluster (Notif/Msg/Cart/Sell/Avatar) + retiring
              the deprecated right SidebarNav is the next nav slice. */}
          <HeaderActions
            isLoggedIn={isLoggedIn}
            onDark={snapStyleNav}
            className="pointer-events-auto hidden md:col-start-4 md:flex md:justify-self-end"
          />
        </div>
      </div>
    </header>
  );
}
